import { IconButton, IPanelStyles, Label, Panel, PanelType, ProgressIndicator, Stack } from "@fluentui/react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../src/taskpane/redux";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { setIsBusySaving } from "../../src/taskpane/redux/modules/savingInBackground";
import { DragAndDropHelper } from "../../utilities/DragAndDropHelper";
import { FileIconType, getFileTypeIconAsUrl, IFileTypeIconOptions } from "@fluentui/react-file-type-icons";
import { IFileUploadProgress } from "../../src/taskpane/interfaces/IFileUploadRequest";
import savingLocationStructureModule from "../../utilities/SavingLocationStructureModule";

const panelStyles: Partial<IPanelStyles> = {
    main: {
        overflow: "hidden"
    },
    content: {
        padding: 15
    }
};

export interface ICMCMFileUploadProgressPanelProps {
    closePanel?: () => void;
    panelIsOpen: boolean;
}

export const CMFileUploadProgressPanel = ({ panelIsOpen, closePanel }: ICMCMFileUploadProgressPanelProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const dispatch = useDispatch();

    const isBusySaving = useSelector((state: RootState) => state.savingInBackground.isBusySaving);
    const errorMessages = useSelector((state: RootState) => state.savingInBackground.errorMessages);
    const savingLocation = useSelector((state: RootState) => state.savingLocation);

    const [hasError, setHasError] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState("");
    const [_, setProgress] = React.useState<number>(0);

    React.useEffect(() => {
        let progressTimer: NodeJS.Timeout;

        if (isBusySaving) {
            progressTimer = setInterval(() => {
                if (DragAndDropHelper.uploadProgress.every(fileUpload => fileUpload.isCancellationRequested)) {
                    dispatch(setIsBusySaving({ isBusySaving: false, isSuccess: false }));
                }
                setProgress(prev => ++prev);
            }, 500);
        }
        else {
            clearTimeout(progressTimer);
        }

        return () => {
            clearTimeout(progressTimer);
        };
    }, [isBusySaving]);

    React.useEffect(() => {
        if (errorMessages?.length > 0) {
            setHasError(true);
            setErrorMessage(errorMessages[0]);
        }
    }, [errorMessages]);

    const getActiveUploads = () => {
        return DragAndDropHelper.uploadProgress
            .filter(fileProgress => !fileProgress.isCancellationRequested);
    }

    const getTotalProgress = () => {
        let totalProgress: number = 0;

        const activeUploads: IFileUploadProgress[] =
            getActiveUploads();

        activeUploads.forEach(fileProgress => {
            totalProgress += fileProgress.progress;
        });

        return totalProgress / activeUploads.length;
    }

    return (
        <Panel
            headerText={languageStrings.Progress}
            isOpen={panelIsOpen}
            onDismiss={() => {
                if (errorMessages?.length > 0) {
                    dispatch(setIsBusySaving({ isBusySaving: false, isSuccess: false }));
                }
                closePanel();
            }}
            isLightDismiss={false}
            closeButtonAriaLabel="Close"
            type={PanelType.custom}
            customWidth={"350px"}
            styles={panelStyles}
        >
            <>
                {hasError &&
                    <div style={{ marginTop: 75, padding: 5 }} className="centerText">
                        <Label>{errorMessage}</Label>
                    </div>
                }
                {!hasError &&
                    <Stack tokens={{ childrenGap: 15 }}>
                        {(DragAndDropHelper.uploadProgress.length > 1)
                            && <Stack horizontal tokens={{ childrenGap: 8 }}>
                                <Stack.Item grow>
                                    <ProgressIndicator
                                        styles={{ root: { marginBottom: 10 } }}
                                        label={`${isBusySaving ? languageStrings.Uploading : languageStrings.Uploaded} ${getActiveUploads().length} ${languageStrings.FilePlural} ${languageStrings.ToDestination} ${savingLocationStructureModule.getSavingDestination()?.displayName ?? savingLocation?.display}`}
                                        description={isBusySaving
                                            ? getTotalProgress() < 1 ? `${(getTotalProgress() * 100).toFixed(0)}% ${languageStrings.Complete.toLowerCase()}` : languageStrings.Finalizing
                                            : DragAndDropHelper.uploadProgress.every(fileUpload => fileUpload.isCancellationRequested) ? languageStrings.Cancelled : languageStrings.Complete}
                                        percentComplete={isBusySaving ? getTotalProgress() : 1} />
                                </Stack.Item>
                                {isBusySaving
                                    && !DragAndDropHelper.uploadProgress.every(fileUpload => fileUpload.progress === 1 || fileUpload.isCancellationRequested)
                                    && <Stack.Item>
                                        <IconButton
                                            iconProps={{ iconName: "Clear" }}
                                            title={`${languageStrings.Cancel} ${getActiveUploads().length} ${languageStrings.FilePlural}`}
                                            onClick={() => DragAndDropHelper.uploadProgress.forEach(fileUpload => fileUpload.isCancellationRequested = true)} />
                                    </Stack.Item>}
                            </Stack>
                        }
                        {DragAndDropHelper.uploadProgress.map((fileProgress, index) => {
                            const iconType: IFileTypeIconOptions = fileProgress.fileExtension
                                ? { extension: fileProgress.fileExtension.trim(), size: 48, imageFileType: "svg" }
                                : { type: FileIconType.folder, size: 48, imageFileType: "svg" };

                            const percentageCompleteDescription: string = fileProgress.progress < 1
                                ? `${(fileProgress.progress * 100).toFixed(0)}% ${languageStrings.Complete.toLowerCase()}`
                                : isBusySaving ? languageStrings.Finalizing : fileProgress.sizeString;

                            return <Stack key={index} horizontal tokens={{ childrenGap: 8 }}>
                                <Stack.Item styles={{ root: { display: "flex", alignItems: "center" } }}>
                                    <img src={getFileTypeIconAsUrl(iconType)} alt={fileProgress.fileName} />
                                </Stack.Item>
                                <Stack.Item grow styles={{ root: { maxWidth: 250 } }}>
                                    <ProgressIndicator
                                        styles={{ root: { marginBottom: 10 }, itemName: { whiteSpace: "normal" } }}
                                        label={DragAndDropHelper.uploadProgress.length > 1 || fileProgress.isCancellationRequested
                                            ? fileProgress.fileName
                                            : <>{isBusySaving ? languageStrings.Uploading : languageStrings.Uploaded} <strong>{fileProgress.fileName}</strong> {languageStrings.ToDestination} {savingLocationStructureModule.getSavingDestination()?.displayName ?? savingLocation?.display}</>}
                                        description={!fileProgress.isCancellationRequested ? percentageCompleteDescription : languageStrings.Cancelled}
                                        percentComplete={!fileProgress.isCancellationRequested ? fileProgress.progress : 0} />
                                </Stack.Item>
                                {fileProgress.progress < 1
                                    && !fileProgress.isCancellationRequested
                                    && <Stack.Item>
                                        <IconButton iconProps={{ iconName: "Clear" }} title={languageStrings.Cancel} onClick={() => fileProgress.isCancellationRequested = true} />
                                    </Stack.Item>}
                            </Stack>
                        })}
                    </Stack>}
            </>
        </Panel>
    )
};